import React, { useEffect, useMemo, useRef, useState } from 'react';
import siteAnalyticsService, { FunnelData, SavedFunnel } from '../../services/siteAnalytics.service';
import { useDateRange, RangeSelector, LoadError, fa, faDecimal, fmtDuration } from './useDateRange';
import { decodePath } from './useDateRange';
import './analytics.css';

const FUNNEL_LABELS: Record<string, string> = {
  signup: '🔐 ثبت‌نام',
  purchase: '🛒 خرید (مشاهده → سبد → پرداخت)',
  sellerSetup: '🏪 راه‌اندازی فروشگاه',
  walletTopup: '💰 شارژ کیف پول',
  chatToPurchase: '💬 چت تا خرید',
};

/** عنوان فارسی برای یک step. type:value */
function stepLabel(raw: string): string {
  const known: Record<string, string> = {
    page_view_register: 'بازدید صفحه ثبت‌نام',
    signup_started: 'شروع ثبت‌نام',
    otp_sent: 'ارسال کد OTP',
    signup_completed: 'تکمیل ثبت‌نام',
    product_view: 'مشاهده محصول',
    add_to_cart: 'افزودن به سبد',
    checkout_started: 'شروع پرداخت',
    purchase: 'خرید نهایی',
    store_apply: 'درخواست فروشندگی',
    store_verified: 'تایید فروشگاه',
    first_listing: 'اولین آگهی',
    wallet_topup_started: 'شروع شارژ',
    wallet_topup_paid: 'پرداخت شارژ',
    wallet_topup_completed: 'تکمیل شارژ',
    chat_start: 'شروع چت',
  };
  const [type, ...rest] = raw.split(':');
  const value = rest.join(':');
  if (type === 'path') return `بازدید مسیر: ${decodePath(value)}`;
  if (type === 'event') return known[value] || `رویداد: ${value}`;
  return known[raw] || raw;
}

/** نمایش یک قیف با bar chart عمودی + percent از step اول */
const FunnelView: React.FC<{ title: string; funnel: FunnelData; emoji?: string }> = ({ title, funnel, emoji }) => {
  const first = funnel.steps[0]?.visitors || 0;
  return (
    <div className="analytics-section">
      <div className="analytics-section-header">
        <h3>
          {emoji} {title}
        </h3>
        <span
          style={{
            fontSize: 13,
            color: funnel.totalConversionRate >= 20 ? 'var(--color-success-dark)' : funnel.totalConversionRate >= 5 ? 'var(--color-warning-dark)' : 'var(--color-error-dark)',
            fontWeight: 700,
          }}
        >
          نرخ تبدیل کل: {faDecimal(funnel.totalConversionRate)}٪
        </span>
      </div>
      {funnel.steps.length === 0 || first === 0 ? (
        <div className="analytics-empty">داده‌ای ثبت نشده برای این قیف</div>
      ) : (
        funnel.steps.map((s, i) => {
          const pct = first > 0 ? Math.round((s.visitors / first) * 100) : 0;
          const color = pct >= 50 ? 'var(--color-success-dark)' : pct >= 25 ? 'var(--color-warning-dark)' : 'var(--color-error-dark)';
          return (
            <div key={i} className="analytics-funnel-step">
              <div className="name" title={s.step}>
                {i + 1}. {stepLabel(s.step)}
              </div>
              <div className="bar">
                <div className="bar-fill" style={{ width: `${pct}%`, background: color }}>
                  {fa(s.visitors)} ({fa(pct)}٪)
                </div>
              </div>
              <div
                className="dropoff"
                style={{
                  color: s.dropOffPct > 50 ? 'var(--color-error-dark)' : s.dropOffPct > 20 ? 'var(--color-warning-dark)' : 'var(--color-text-secondary)',
                }}
              >
                {i > 0 ? (s.dropOffPct > 0 ? `▼ ${fa(s.dropOffPct)}٪ افت` : '— ادامه') : '—'}
                {i > 0 && typeof s.medianLagSec === 'number' && s.medianLagSec > 0 && (
                  <div style={{ fontSize: 10.5, color: 'var(--color-text-secondary)', fontWeight: 400 }}>
                    ⏱ {fmtDuration(s.medianLagSec)}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

/** پیشنهادهای آماده — هم‌راستا با قیف‌های استاندارد بک‌اند (getStandardFunnels) */
const PRESETS: Array<{ label: string; steps: string[] }> = [
  { label: 'ثبت‌نام', steps: ['path:^/register', 'event:signup_completed'] },
  { label: 'خرید کامل', steps: ['path:^/products?/', 'event:add_to_cart', 'path:^/checkout', 'event:purchase'] },
  { label: 'راه‌اندازی فروشگاه', steps: ['event:signup_completed', 'event:store_apply', 'event:store_verified', 'event:first_listing'] },
  { label: 'شارژ کیف پول', steps: ['path:^/user/wallet', 'event:wallet_topup_started', 'event:wallet_topup_completed'] },
  { label: 'بلاگ → محصول', steps: ['path:^/blog/', 'path:^/products?/'] },
];

type StepDraft = { id: number; type: 'event' | 'path'; value: string };
// کلید پایدار برای هر مرحله (به‌جای index) — حذف مرحلهٔ میانی state ورودی‌ها را قاطی نمی‌کند
let stepSeq = 0;
const mkStep = (type: 'event' | 'path', value: string): StepDraft => ({ id: ++stepSeq, type, value });

const StepRow: React.FC<{
  step: StepDraft;
  onChange: (s: StepDraft) => void;
  onRemove: () => void;
  index: number;
}> = ({ step, onChange, onRemove, index }) => (
  <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
    <span style={{ width: 28, color: 'var(--color-text-secondary)', fontWeight: 600 }}>{fa(index + 1)}.</span>
    <select
      value={step.type}
      onChange={(e) => onChange({ ...step, type: e.target.value as 'event' | 'path' })}
      style={{ padding: 6, border: '1px solid var(--color-border)', borderRadius: 6 }}
    >
      <option value="event">رویداد</option>
      <option value="path">مسیر صفحه</option>
    </select>
    <input
      type="text"
      value={step.value}
      onChange={(e) => onChange({ ...step, value: e.target.value })}
      placeholder={step.type === 'event' ? 'مثلاً: product_view' : 'مثلاً: ^/product/  یا  /checkout'}
      style={{ flex: 1, padding: 7, border: '1px solid var(--color-border)', borderRadius: 6, direction: 'ltr', textAlign: 'left' }}
    />
    <button onClick={onRemove} style={{ color: 'var(--color-error-dark)', background: 'none', border: 'none', cursor: 'pointer' }}>
      حذف
    </button>
  </div>
);

const SiteAnalyticsFunnels: React.FC = () => {
  const dr = useDateRange('30d');
  const [data, setData] = useState<Record<string, FunnelData> | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  const [steps, setSteps] = useState<StepDraft[]>([
    mkStep('path', '^/products?/'),
    mkStep('event', 'add_to_cart'),
    mkStep('event', 'purchase'),
  ]);
  const [customResult, setCustomResult] = useState<FunnelData | null>(null);
  const [customLoading, setCustomLoading] = useState(false);

  const [saved, setSaved] = useState<SavedFunnel[]>([]);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setErr(null);
    try {
      const r = await siteAnalyticsService.getStandardFunnels(dr.range);
      if (reqId !== reqIdRef.current) return;
      setData(r.funnels);
    } catch (e: any) {
      if (reqId !== reqIdRef.current) return;
      setErr(e?.message || 'خطا در دریافت داده');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  const loadSaved = async () => {
    try {
      // پرمیشن جدا (admin_analytics_funnel_custom) دارد؛ 403 نباید کل صفحه را ریدایرکت کند —
      // فقط بخش «قیف‌های ذخیره‌شده» پنهان می‌ماند.
      setSaved(await siteAnalyticsService.listSavedFunnels({ silentForbidden: true }));
    } catch {
      /* ignore — بخش ذخیره‌شده‌ها نمایش داده نمی‌شود */
    }
  };

  useEffect(() => {
    load();
    // 🔧 قیف سفارشیِ روی صفحه با بازه‌ی قبلی محاسبه شده — با تغییر بازه دوباره
    // اجرا می‌شود تا دو بازه‌ی متفاوت کنار هم نمایش داده نشوند (گمراه‌کننده بود)
    if (customResult) {
      const encoded = steps.filter((s) => s.value.trim().length > 0).map((s) => `${s.type}:${s.value.trim()}`);
      if (encoded.length >= 2) {
        setCustomLoading(true);
        siteAnalyticsService
          .getCustomFunnel(encoded, dr.range)
          .then((r) => setCustomResult(r))
          .catch(() => setCustomResult(null))
          .finally(() => setCustomLoading(false));
      } else {
        setCustomResult(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dr.range.from, dr.range.to, dr.refreshKey]);

  useEffect(() => {
    loadSaved();
  }, []);

  const encodedSteps = () =>
    steps.filter((s) => s.value.trim().length > 0).map((s) => `${s.type}:${s.value.trim()}`);

  const saveCurrent = async () => {
    const encoded = encodedSteps();
    if (!saveName.trim() || encoded.length < 2) return;
    setSaving(true);
    try {
      await siteAnalyticsService.createSavedFunnel({ name: saveName.trim(), steps: encoded });
      setSaveName('');
      await loadSaved();
    } catch (e: any) {
      setErr(e?.message || 'خطا در ذخیرهٔ قیف');
    } finally {
      setSaving(false);
    }
  };

  const runSaved = async (f: SavedFunnel) => {
    setSteps(
      f.steps.map((p) => {
        const [type, ...rest] = p.split(':');
        return mkStep(type === 'path' ? 'path' : 'event', rest.join(':'));
      })
    );
    setCustomLoading(true);
    try {
      setCustomResult(await siteAnalyticsService.getCustomFunnel(f.steps, dr.range));
    } catch (e: any) {
      setErr(e?.message || 'خطا در اجرای قیف');
    } finally {
      setCustomLoading(false);
    }
  };

  const deleteSaved = async (f: SavedFunnel) => {
    if (!window.confirm(`حذف قیف ذخیره‌شدهٔ «${f.name}»؟`)) return;
    try {
      await siteAnalyticsService.deleteSavedFunnel(f._id);
      await loadSaved();
    } catch (e: any) {
      setErr(e?.message || 'خطا در حذف قیف');
    }
  };

  const runCustom = async () => {
    const cleaned = steps.filter((s) => s.value.trim().length > 0);
    if (cleaned.length < 2) return;
    const encoded = cleaned.map((s) => `${s.type}:${s.value.trim()}`);
    setCustomLoading(true);
    try {
      const r = await siteAnalyticsService.getCustomFunnel(encoded, dr.range);
      setCustomResult(r);
    } catch (e: any) {
      setErr(e?.message || 'خطا در اجرای قیف سفارشی');
    } finally {
      setCustomLoading(false);
    }
  };

  const applyPreset = (preset: string[]) => {
    setSteps(
      preset.map((p) => {
        const [type, ...rest] = p.split(':');
        return mkStep(type === 'path' ? 'path' : 'event', rest.join(':'));
      })
    );
  };

  const totalEvents = useMemo(() => Object.values(data || {}).reduce((s, f) => s + (f.steps[0]?.visitors || 0), 0), [data]);

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h2>قیف‌های تبدیل</h2>
        <div className="analytics-controls">
          <RangeSelector {...dr} />
          <button onClick={dr.refresh} disabled={loading}>
            {loading ? 'در حال بارگذاری…' : 'بروزرسانی'}
          </button>
        </div>
      </div>

      {err && <LoadError message={err} onRetry={load} />}

      {data && totalEvents === 0 && (
        <div className="analytics-empty" style={{ marginBottom: 16 }}>
          هنوز هیچ event کافی برای قیف‌های پیش‌فرض ثبت نشده. اگر tracker تازه نصب شده، چند ساعت صبر کنید
          تا کاربران واقعی صفحه‌ها را باز کنند و رویدادهای purchase / add_to_cart / signup_completed
          از سمت کلاینت emit شوند.
        </div>
      )}

      {data &&
        Object.entries(data).map(([key, funnel]) => (
          <FunnelView key={key} title={FUNNEL_LABELS[key] || key} funnel={funnel} />
        ))}

      <div className="analytics-section">
        <div className="analytics-section-header">
          <h3>🧪 قیف سفارشی</h3>
        </div>

        <div style={{ background: 'var(--color-bg-default)', padding: 14, borderRadius: 10, marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 10, lineHeight: 1.7 }}>
            <strong>راهنما:</strong> هر مرحله می‌تواند یکی از این دو نوع باشد:
            <br />
            • <strong>رویداد</strong> — نام رویداد ارسالی از سمت کلاینت (مثل <code>add_to_cart</code>).
            <br />
            • <strong>مسیر صفحه</strong> — الگوی regex روی URL؛ لنگرهای <code>^</code> و <code>$</code> پشتیبانی
            می‌شوند. مثلاً <code>{'^/products?/'}</code> یعنی صفحاتی که با <code>/product/</code> یا{' '}
            <code>/products/</code> شروع می‌شوند، <code>/checkout$</code> یعنی مسیری که به <code>/checkout</code> ختم
            می‌شود، و الگوی بدون لنگر (مثل <code>wallet</code>) یعنی «هر جای مسیر شامل آن باشد».
          </div>

          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 6 }}>پیشنهادهای آماده:</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => applyPreset(p.steps)}
                  style={{
                    padding: '4px 10px',
                    fontSize: 12,
                    background: 'var(--color-bg-paper)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 14,
                    cursor: 'pointer',
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {steps.map((s, i) => (
            <StepRow
              key={s.id}
              step={s}
              index={i}
              onChange={(ns) =>
                setSteps((prev) => prev.map((p, idx) => (idx === i ? ns : p)))
              }
              onRemove={() => setSteps((prev) => prev.filter((_, idx) => idx !== i))}
            />
          ))}

          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button
              onClick={() => setSteps((prev) => [...prev, mkStep('event', '')])}
              style={{ padding: '5px 12px', fontSize: 13 }}
            >
              + افزودن مرحله
            </button>
            <button
              onClick={runCustom}
              disabled={customLoading || steps.filter((s) => s.value.trim()).length < 2}
              style={{
                padding: '5px 16px',
                fontSize: 13,
                background: 'var(--color-primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {customLoading ? 'در حال محاسبه…' : 'اجرای قیف'}
            </button>

            <div style={{ flex: 1 }} />
            <input
              type="text"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="نام برای ذخیره…"
              style={{ padding: 7, border: '1px solid var(--color-border)', borderRadius: 6, width: 160 }}
            />
            <button
              onClick={saveCurrent}
              disabled={saving || !saveName.trim() || steps.filter((s) => s.value.trim()).length < 2}
              style={{ padding: '5px 12px', fontSize: 13 }}
            >
              {saving ? 'در حال ذخیره…' : '💾 ذخیره قیف'}
            </button>
          </div>
        </div>

        {customResult && <FunnelView title="نتیجه قیف سفارشی" funnel={customResult} emoji="📊" />}
      </div>

      {saved.length > 0 && (
        <div className="analytics-section">
          <div className="analytics-section-header">
            <h3>💾 قیف‌های ذخیره‌شده</h3>
          </div>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>نام</th>
                <th>مراحل</th>
                <th style={{ width: 160 }}>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {saved.map((f) => (
                <tr key={f._id}>
                  <td style={{ fontWeight: 500 }}>{f.name}</td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    {f.steps.map((s) => stepLabel(s)).join(' → ')}
                  </td>
                  <td>
                    <button onClick={() => runSaved(f)} style={{ padding: '4px 10px', fontSize: 12, marginInlineEnd: 6 }}>
                      اجرا
                    </button>
                    <button
                      onClick={() => deleteSaved(f)}
                      style={{ padding: '4px 10px', fontSize: 12, color: 'var(--color-error-dark)', background: 'none', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer' }}
                    >
                      حذف
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SiteAnalyticsFunnels;
